import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { z } from "zod";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import { fetchFastApi } from "../../../../api/fastapi";
import { useAudit } from "../../../../contexts/auditContext";
import { useAuth } from "../../../../contexts/authContext";
import { useProcedureSessionTableShell } from "../../../../hooks/audit/useProcedureSessionTableShell";
import { useSourceRow } from "../../../../hooks/audit/useSourceRow";
import { useControl } from "../../../../hooks/audit/useControl";
import { useWorkpaperData } from "../../../../hooks/useWorkpaperData";
import { useHookCache } from "../../../../lib/hookCache";
import { AuditSession, SourceRow } from "../../../../models/audit/auditSession";
import { Citation } from "../../../../models/audit/citation";
import {
  CellCitationDetail,
  SessionSourceRowData,
  SessionSourceRowDataWithCitations,
  WorkpaperDataSlim,
} from "../../../../models/audit/workpaperData";
import type { BoundingBox } from "../../../extraction/viewExtraction/FileViewer";
import { LinkingData } from "../../../extraction/viewExtraction/PdfViewer";
import { getOpenFoldOut } from "../utils/foldOutSelection";
import { WorkPaperTableV2 } from "./WorkPaperTableV2";
import { SupportColSidePanelV2 } from "./SupportColSidePanelV2";
import { CheckAnalysisV2 } from "./CheckAnalysisV2";
import { AttributeTestAnalysis } from "./AttributeTestAnalysis";
import { AnalyticsResultAnalysis } from "./AnalyticsResultAnalysis";
import { selectionIsInAnalyticsNamespace } from "../../../../models/audit/analyticsTranslator";
import { AttributeTestFoldOut } from "./AttributeTestFoldOut";
import { SupportCellFoldOut } from "./SupportCellFoldOut";
import { CitationDetailHydrator } from "./CitationDetailHydrator";

type WorkpaperDataType = WorkpaperDataSlim;
type WorkpaperCell = WorkpaperDataType["data"][string][number];

interface ProcedureSessionWorkPaperProps {
  procedureSessionId: string;
  /**
   * Side-panel state is lifted to the shell so the right `ResizablePanel`
   * can be a sibling of `AuditWorkpaperLeftPanel` (full viewport height).
   * `SupportColSidePanelV2` is rendered into `sidePanelHost` via portal.
   */
  showRightPanel: boolean;
  setShowRightPanel: Dispatch<SetStateAction<boolean>>;
  sidePanelHost: HTMLElement | null;
}

/**
 * Per-procedure work-paper child. The thin `AuditWorkPaperViewV2` shell
 * stacks one of these per enabled procedure session, each with a stable
 * `key={procedureSessionId}`, so every instance stays mounted and keeps its
 * own view-local state (`currentBoundingBoxes`, `currentPage`, etc.) across
 * selection changes. Opening a fold-out hides the non-active instances
 * rather than unmounting them, so returning to one restores its state intact.
 *
 * Mutation handlers stay inline here and write directly to the per-psId
 * workpaper-data cache entry this component owns.
 */
export const ProcedureSessionWorkPaper = ({
  procedureSessionId,
  showRightPanel,
  setShowRightPanel,
  sidePanelHost,
}: ProcedureSessionWorkPaperProps) => {
  // Workpaper data is now read directly from the shared `HookCacheProvider`
  // by `useWorkpaperData(psId)`. The hook registers a fetcher under
  // `["workpaper-data", psId]`, so `invalidate(["workpaper-data", psId])`
  // from `useSessionRunsTransitions` flips this entry and we re-render
  // automatically. Mutations write to the cache via `setEntry`; error-path
  // rollbacks issue `invalidate(["workpaper-data", psId])`.
  const { workpaperData, loading: workpaperDataLoading } =
    useWorkpaperData(procedureSessionId);
  const {
    controlSession,
    updateCurrentDocument,
    selectedDocument,
    currentSelectedDocumentUrl,
    currentSelection,
    setCurrentSelection,
  } = useAudit();
  const { user } = useAuth();

  // Primary data source: the light table-shell. Single-row hydration happens
  // lazily via `useSourceRow(psId, srId)` below, for the currently-selected
  // row only, which is what the side panels need.
  const { data: tableShell } =
    useProcedureSessionTableShell(procedureSessionId);

  // Heavy single-row hydration: only fires from the instance whose
  // `procedureSessionId` matches `currentSelection.procedureSessionId`.
  // Without this guard, every mounted `ProcedureSessionWorkPaper` (one
  // per enabled procedure) would re-render on selection change and each
  // would subscribe `useSourceRow` to a new per-procedure cache key,
  // fanning out N fetches (N-1 of which 404 because the row only lives
  // on one procedure).
  const isSelectedProcedure =
    currentSelection?.procedureSessionId === procedureSessionId;
  const { data: selectedSourceRow } = useSourceRow(
    procedureSessionId,
    isSelectedProcedure ? currentSelection?.sourceRowId : undefined,
  );

  // The control's own `attributes` are carried by neither the control-session
  // envelope nor the per-procedure table-shell — the table-shell's
  // `procedure.attributes` is a different collection under the same name — so
  // `useControl` is the only source for them. This call retires once the
  // envelope's `control` field carries them.
  const { data: hydratedControl } = useControl(controlSession?.controlId);
  const attributes =
    controlSession?.workStep?.attributes ?? hydratedControl?.attributes ?? [];

  // The inline fold-out is the auditor-trail (Evidence Walk V3) UI — feature
  // flagged. When disabled for the workspace, both fold-outs stay off and the
  // pre-feature behaviour renders (the right-side AttributeTestAnalysis sheet and
  // the SupportColSidePanelV2). Defaults off while the envelope loads.
  const auditorTrailEnabled = controlSession?.auditorTrailEnabled === true;

  // Synthesised `AuditSession`-shaped object for the side panels that
  // expect the heavy prop. The dry table-shell drives the
  // top-level fields (`procedure`, `documents`) and the lazy single
  // `useSourceRow` drives the one source-row entry the side panel
  // actually reads. The remaining (un-selected) rows are absent — side
  // panels look up by `sourceRowId`, so they only ever see the row they
  // care about. This keeps the side-panel signatures unchanged while
  // fetching only the row on screen.
  const auditSession = useMemo<AuditSession | undefined>(() => {
    if (!tableShell) return undefined;
    const envelope = tableShell.procedureSession;
    const sourceRows: SourceRow[] = selectedSourceRow
      ? [selectedSourceRow]
      : [];
    return {
      id: envelope.id,
      procedureId: envelope.procedureId,
      name: envelope.name,
      controlSessionId: envelope.controlSessionId,
      state: envelope.state,
      documentsState: envelope.documentsState,
      testsState: envelope.testsState,
      sourceRowsState: envelope.sourceRowsState,
      workPaperState: envelope.workPaperState,
      createdAt: envelope.createdAt,
      updatedAt: envelope.updatedAt,
      procedure: tableShell.procedure,
      documents: tableShell.documents,
      sourceRows,
    };
  }, [tableShell, selectedSourceRow]);

  // View-local state — each stacked instance keeps its own copy, since the
  // parent gives every one a stable `key={procedureSessionId}` and leaves it
  // mounted. `showRightPanel` is the one exception: it lives on the shell so
  // the right ResizablePanel can be a sibling of the chrome.
  const [currentBoundingBoxes, setCurrentBoundingBoxes] = useState<
    | {
        id?: string;
        boundingBox: BoundingBox | undefined;
        elementId: string;
        pageWidth: number;
        pageHeight: number;
        pageNumber: number;
        shortSummary?: string;
        documentId?: string;
        timestampSeconds?: number;
      }[]
    | undefined
  >(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBoundingIndex, setSelectedBoundingIndex] = useState<number>(0);
  // Workpaper-pane open state, held here (not remounted per cell) so it survives
  // the fold-out's per-cell remount — clicking another attribute keeps it open.
  const [gridExpanded, setGridExpanded] = useState(false);
  // Pane proportions held in a ref (not state) so reporting them on every resize
  // frame doesn't re-render this tree; read on the next cell-click remount.
  const paneLayoutRef = useRef<number[] | null>(null);
  // Grid scroll offsets, lifted for the same reason: the fold-out's per-cell
  // remount resets fresh scroll containers to the top-left corner, so the table
  // (horizontal) and the fold-out grid pane (vertical) restore these on mount —
  // clicking a cell never moves the workpaper.
  const gridScrollLeftRef = useRef(0);
  const gridScrollTopRef = useRef(0);
  // Which fold-out (if any) the selection opens — shared predicate with the
  // shell, which uses it to hide sibling procedure tables only while a
  // fold-out is actually open.
  const openFoldOut = getOpenFoldOut(
    auditorTrailEnabled,
    currentSelection,
    showRightPanel,
  );
  // The lifted workpaper-pane layout above survives the per-cell remount so it
  // stays put while clicking between cells in an open fold-out. It should NOT
  // survive closing back to the base table — reset it whenever the result
  // fold-out is closed, so a fresh cell click starts with the workpaper collapsed
  // again instead of permanently reopening the three-pane layout.
  //
  // Both result kinds share the layout because they share the fold-out: an
  // attribute-test cell and an analytics cell open the same three panes, and an
  // auditor moving between them is moving within one workpaper.
  const resultFoldOutOpen =
    isSelectedProcedure &&
    (openFoldOut === "attr-test" || openFoldOut === "analytics");
  useEffect(() => {
    if (!resultFoldOutOpen) {
      setGridExpanded(false);
      paneLayoutRef.current = null;
    }
  }, [resultFoldOutOpen]);

  // Same lifted layout for the support-cell fold-out (two panes, so a separate
  // ref from the attribute-test one) — a resized workpaper : viewer split
  // survives the per-cell remount instead of snapping back to the even split.
  const supportFoldOutOpen = isSelectedProcedure && openFoldOut === "support";
  const [supportGridExpanded, setSupportGridExpanded] = useState(true);
  const supportPaneLayoutRef = useRef<number[] | null>(null);
  useEffect(() => {
    if (!supportFoldOutOpen) {
      setSupportGridExpanded(true);
      supportPaneLayoutRef.current = null;
    }
  }, [supportFoldOutOpen]);

  const { setEntry, getEntry, invalidate } = useHookCache();

  const handleCheckSelectionV2 = ({
    sourceRowId,
    documentId,
    checkResultId,
    supportColumnId,
    value,
    evalColumnId,
  }: {
    sourceRowId?: string;
    attributeId?: string;
    documentId?: string;
    checkResultId?: string;
    supportColumnId?: string;
    value?: string;
    evalColumnId?: string;
  }) => {
    if (
      !sourceRowId &&
      !documentId &&
      !checkResultId &&
      !value &&
      !supportColumnId
    ) {
      setCurrentBoundingBoxes(undefined);
      setShowRightPanel(false);
      return;
    }
    setCurrentSelection({
      procedureSessionId,
      sourceRowId,
      documentId,
      checkResultId,
      value,
      supportColumnId,
      evalColumnId,
    });
    // When the auditor-trail flag is on, support cells open the inline
    // three-pane fold-out (driven off `currentSelection`), so the legacy
    // right-side panel stays closed. Flag off → keep the pre-feature behaviour
    // of opening the SupportColSidePanelV2 for a clicked support cell.
    setShowRightPanel(auditorTrailEnabled ? false : !!documentId);
    // Reset the citation-panel state synchronously, and only when the selected
    // cell actually changed: a same-cell re-click must keep the hydrated boxes,
    // because `CitationDetailHydrator` re-pushes them only when its cached
    // citation data changes. The reset must happen here (pre-render), never in
    // a selection-keyed effect — parent effects run after the hydrator's
    // populate effect and would wipe warm-cache revisits (see
    // `CitationDetailHydrator`).
    const cellChanged =
      currentSelection?.sourceRowId !== sourceRowId ||
      currentSelection?.supportColumnId !== supportColumnId ||
      currentSelection?.checkResultId !== checkResultId;
    if (cellChanged) {
      setCurrentBoundingBoxes(undefined);
      setCurrentPage(1);
      setSelectedBoundingIndex(0);
    }
  };

  const createCellEditHandler = useCallback(
    (auditSessionArg: AuditSession) => {
      return async (
        newValue: string,
        sourceRowId: string,
        supportColumnId: string,
      ) => {
        if (!auditSessionArg || !sourceRowId || !supportColumnId) {
          toast.error("Missing required information for cell update");
          return;
        }

        try {
          const existingData = workpaperData?.data[sourceRowId]?.find(
            (data) => data.supportColumnId === supportColumnId,
          );
          // Workpaper-lazy-load: the slim `workpaperData` does not carry
          // `citations[]`. Read the authoritative list from the per-cell
          // citation cache instead — `getEntry` returns `undefined` when
          // the user has not opened the citation viewer yet, in which case
          // we fall back to `[]` (preserving the pre-existing
          // `existingData?.citations || []` semantics for that case).
          const existingCitations: Citation[] =
            getEntry<CellCitationDetail>([
              "procedure-session",
              procedureSessionId,
              "support-cell-citations",
              sourceRowId,
              supportColumnId,
            ])?.data?.citations ?? [];

          const supportData: z.infer<typeof SessionSourceRowDataWithCitations> =
            {
              id: crypto.randomUUID(),
              session_id: auditSessionArg.id,
              attribute_id: null,
              support_column_id: supportColumnId,
              source_row_id: sourceRowId,
              data: newValue,
              bounding_box: existingData?.sourceDocument?.boundingBox || null,
              source_document_id: existingData?.sourceDocument?.id || null,
              source_element:
                existingData?.sourceDocument?.sourceElementId || null,
              page_number: existingData?.sourceDocument?.pageNumber || null,
              page_width: existingData?.sourceDocument?.pageWidth || null,
              page_height: existingData?.sourceDocument?.pageHeight || null,
              user_id: user?.id || null,
              locked_by: existingData?.lockedBy || null,
              column_version: existingData?.columnVersion || 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              citations: existingCitations,
            };

          setEntry<WorkpaperDataType>(
            ["workpaper-data", procedureSessionId],
            (prev) => {
              const updatedData = { ...prev };
              const sourceRowData = [...(updatedData.data[sourceRowId] || [])];
              const existingIndex = sourceRowData.findIndex(
                (item) => item.supportColumnId === supportColumnId,
              );
              // Workpaper-lazy-load: omit `citations` from the slim cell
              // entry — the per-cell citation cache owns that list now.
              const updatedDataEntry: WorkpaperCell = {
                value: newValue,
                sourceDocument: existingData?.sourceDocument || {
                  id: "",
                  name: "",
                  boundingBox: null,
                  sourceElementId: null,
                  pageNumber: null,
                  pageWidth: null,
                  pageHeight: null,
                },
                testResult: null,
                sourceRowId: sourceRowId,
                attributeId: null,
                lockedBy: user?.id || null,
                supportColumnId: supportColumnId,
                columnVersion: existingData?.columnVersion ?? 0,
              };
              if (existingIndex >= 0) {
                sourceRowData[existingIndex] = updatedDataEntry;
              } else {
                sourceRowData.push(updatedDataEntry);
              }
              updatedData.data = {
                ...updatedData.data,
                [sourceRowId]: sourceRowData,
              };
              return updatedData;
            },
          );

          if (
            currentSelection?.sourceRowId === sourceRowId &&
            currentSelection?.supportColumnId === supportColumnId
          ) {
            setCurrentSelection({
              ...currentSelection,
              value: newValue,
            });
          }

          await fetchFastApi({
            path: `/matching/match-session/${auditSessionArg.id}/source-row/${sourceRowId}/support-data-cell/update-value`,
            method: "POST",
            body: { support_column: supportData },
          });

          toast.success("Cell updated successfully");
        } catch (error) {
          console.error("Error updating cell:", error);
          toast.error("Failed to update cell");
          void invalidate(["workpaper-data", procedureSessionId]);
        }
      };
    },
    [
      workpaperData,
      setEntry,
      currentSelection,
      setCurrentSelection,
      invalidate,
      user,
      procedureSessionId,
      getEntry,
    ],
  );

  const handleLinkingComplete = useCallback(
    async (linkingData: LinkingData) => {
      if (!auditSession || !currentSelection?.sourceRowId) {
        toast.error("Missing session or source row information");
        return;
      }
      const pages = currentSelectedDocumentUrl?.documentExtraction?.pages;
      let currentPageData = null;
      if (pages) {
        currentPageData = pages[linkingData.pageNumber - 1];
      }

      try {
        const sourceRowId = currentSelection.sourceRowId;
        const attributeId = currentSelection.attributeId;
        const supportColumnId = currentSelection.supportColumnId;
        const documentId = currentSelection.documentId || "";
        const elementId = currentBoundingBoxes?.[0]?.elementId || "";
        // Pair the box with the dims it was drawn in (`linkingData`) so it stays
        // put on re-render; fall back to the current page's dims.
        const pageWidth =
          linkingData.pageWidth ||
          currentPageData?.width ||
          currentBoundingBoxes?.[0]?.pageWidth ||
          null;
        const pageHeight =
          linkingData.pageHeight ||
          currentPageData?.height ||
          currentBoundingBoxes?.[0]?.pageHeight ||
          null;

        const entry = workpaperData?.data[sourceRowId]?.find(
          (item) =>
            (attributeId && item.attributeId === attributeId) ||
            (supportColumnId && item.supportColumnId === supportColumnId),
        );
        // Workpaper-lazy-load: the citation list lives on the per-cell
        // citation cache, not the slim cell entry. The user must have opened
        // the citation viewer to be linking (so the cache is hydrated), but
        // fall back to `[]` defensively.
        const existingCitationsForCell: Citation[] = supportColumnId
          ? (getEntry<CellCitationDetail>([
              "procedure-session",
              procedureSessionId,
              "support-cell-citations",
              sourceRowId,
              supportColumnId,
            ])?.data?.citations ?? [])
          : [];

        const isEditingExisting =
          !!linkingData.editingBoundingBoxId ||
          linkingData.editingBoundingBoxIndex !== undefined;

        let updatedCitations: Citation[] = [...existingCitationsForCell];
        let editedCitationId: string | undefined = undefined;
        if (isEditingExisting) {
          const targetIdx = linkingData.editingBoundingBoxId
            ? updatedCitations.findIndex(
                (c) => c.id === linkingData.editingBoundingBoxId,
              )
            : (linkingData.editingBoundingBoxIndex ?? -1);
          if (targetIdx >= 0 && updatedCitations[targetIdx]) {
            const prev = updatedCitations[targetIdx];
            editedCitationId = prev.id || linkingData.editingBoundingBoxId;
            updatedCitations[targetIdx] = {
              ...prev,
              citationOrder: prev.citationOrder ?? updatedCitations.length + 1,
              boundingBox: linkingData.boundingBox,
              pageNumber: linkingData.pageNumber,
              pageWidth: pageWidth,
              pageHeight: pageHeight,
              updatedAt: new Date().toISOString(),
            };
          } else {
            const nc = {
              id: crypto.randomUUID(),
              documentId,
              encodedCitationString: elementId,
              shortSummary: null,
              boundingBox: linkingData.boundingBox,
              pageNumber: linkingData.pageNumber,
              pageWidth,
              pageHeight,
              citationOrder: updatedCitations.length + 1,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            };
            updatedCitations = [...updatedCitations, nc];
            editedCitationId = nc.id;
          }
        } else {
          const nc = {
            id: crypto.randomUUID(),
            documentId,
            encodedCitationString: elementId,
            shortSummary: null,
            boundingBox: linkingData.boundingBox,
            pageNumber: linkingData.pageNumber,
            pageWidth,
            pageHeight,
            citationOrder: updatedCitations.length + 1,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          updatedCitations = [...updatedCitations, nc];
          editedCitationId = nc.id;
        }

        const supportData: z.infer<typeof SessionSourceRowData> = {
          id: crypto.randomUUID(),
          session_id: auditSession.id,
          attribute_id: attributeId || null,
          support_column_id: supportColumnId || null,
          source_row_id: sourceRowId,
          data: linkingData.includeValue
            ? linkingData.value
            : currentSelection.value || "",
          bounding_box: null,
          source_document_id: documentId || null,
          source_element: elementId || null,
          page_number: linkingData.pageNumber,
          page_width: pageWidth,
          page_height: pageHeight,
          user_id: user?.id || null,
          locked_by: entry?.lockedBy || null,
          column_version: entry?.columnVersion || 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setEntry<WorkpaperDataType>(
          ["workpaper-data", procedureSessionId],
          (prev) => {
            const next = { ...prev };
            const list = [...(next.data[sourceRowId] || [])];
            const idx = list.findIndex(
              (item) =>
                (attributeId && item.attributeId === attributeId) ||
                (supportColumnId && item.supportColumnId === supportColumnId),
            );
            const updatedSourceDocument =
              idx >= 0 && list[idx].sourceDocument
                ? list[idx].sourceDocument
                : {
                    id: documentId,
                    name: selectedDocument?.fileName || "",
                    boundingBox: linkingData.boundingBox,
                    sourceElementId: elementId || null,
                    pageNumber: linkingData.pageNumber,
                    pageWidth,
                    pageHeight,
                  };
            // Workpaper-lazy-load: the slim cell entry does not carry
            // `citations[]`; the per-cell citation cache below owns it.
            const nextEntry: WorkpaperCell = {
              value: linkingData.includeValue
                ? linkingData.value
                : currentSelection.value || "",
              sourceDocument: updatedSourceDocument,
              testResult: null,
              sourceRowId,
              lockedBy: user?.id || null,
              attributeId: attributeId || null,
              supportColumnId: supportColumnId || null,
              columnVersion: idx >= 0 ? list[idx].columnVersion : 0,
            };
            if (idx >= 0) list[idx] = nextEntry;
            else list.push(nextEntry);
            next.data = { ...next.data, [sourceRowId]: list };
            return next;
          },
        );

        // Workpaper-lazy-load: keep the per-cell citation cache in sync so
        // `CitationDetailHydrator` / `useCellCitationDetail` consumers see
        // the optimistic edit immediately.
        if (supportColumnId) {
          setEntry<CellCitationDetail>(
            [
              "procedure-session",
              procedureSessionId,
              "support-cell-citations",
              sourceRowId,
              supportColumnId,
            ],
            () => ({ citations: updatedCitations }),
          );
        }

        setCurrentBoundingBoxes((prev) => {
          const prevList = prev || [];
          if (isEditingExisting) {
            const next = prevList.map((bb) =>
              bb.id === linkingData.editingBoundingBoxId
                ? {
                    ...bb,
                    boundingBox: linkingData.boundingBox,
                    pageNumber: linkingData.pageNumber,
                    pageWidth: pageWidth || 1,
                    pageHeight: pageHeight || 1,
                  }
                : bb,
            );
            const selectedIdx = next.findIndex(
              (b) => b.id === editedCitationId,
            );
            if (selectedIdx >= 0) setSelectedBoundingIndex(selectedIdx);
            setCurrentPage(linkingData.pageNumber);
            return next;
          } else {
            const newBox = {
              id: editedCitationId,
              boundingBox: linkingData.boundingBox,
              pageNumber: linkingData.pageNumber,
              pageWidth: pageWidth || 1,
              pageHeight: pageHeight || 1,
              elementId: elementId || "",
            };
            const next = [...prevList, newBox];
            setCurrentPage(linkingData.pageNumber);
            setSelectedBoundingIndex(next.length - 1);
            return next;
          }
        });

        if (linkingData.includeValue) {
          setCurrentSelection({
            ...currentSelection,
            value: linkingData.value,
          });
        }

        const normalizedCitations: Citation[] = updatedCitations.map((c) => ({
          id: c.id || crypto.randomUUID(),
          documentId: c.documentId || documentId,
          encodedCitationString: c.encodedCitationString || elementId,
          shortSummary: c.shortSummary ?? null,
          boundingBox: c.boundingBox ?? null,
          pageNumber: c.pageNumber ?? linkingData.pageNumber,
          pageWidth: c.pageWidth ?? pageWidth,
          pageHeight: c.pageHeight ?? pageHeight,
          citationOrder: c.citationOrder || 0,
          createdAt: c.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));

        await fetchFastApi({
          path: `/matching/match-session/${auditSession.id}/source-row/${sourceRowId}/support-data-cell/update-value`,
          method: "POST",
          body: {
            support_column: { ...supportData, citations: normalizedCitations },
          },
        });

        toast.success("Support data cell updated successfully");
      } catch (error) {
        console.error("Error updating support data cell:", error);
        toast.error("Failed to update support data cell");
      }
    },
    [
      auditSession,
      currentSelection,
      setCurrentSelection,
      selectedDocument?.fileName,
      currentSelectedDocumentUrl,
      workpaperData?.data,
      currentBoundingBoxes,
      user,
      procedureSessionId,
      getEntry,
      setEntry,
    ],
  );

  const handleDeleteBoundingBox = useCallback(
    async ({ id, index }: { id?: string; index: number }) => {
      const sourceRowId = currentSelection?.sourceRowId;
      if (!auditSession || !sourceRowId) {
        toast.error("Missing session or source row information");
        return;
      }

      setCurrentBoundingBoxes((prev) => {
        if (!prev || prev.length === 0) return prev;
        let next = prev;
        if (id) {
          next = prev.filter((bb) => bb.id !== id);
        } else if (index >= 0 && index < prev.length) {
          next = [...prev.slice(0, index), ...prev.slice(index + 1)];
        }
        if (next.length > 0) {
          const newSelected = Math.max(0, Math.min(next.length - 1, index - 1));
          setSelectedBoundingIndex(newSelected);
          const page = next[newSelected]?.pageNumber || 1;
          setCurrentPage(page);
        } else {
          setSelectedBoundingIndex(0);
        }
        return next;
      });

      const entry = workpaperData?.data[sourceRowId]?.find(
        (item) =>
          (currentSelection?.attributeId &&
            item.attributeId === currentSelection?.attributeId) ||
          (currentSelection?.supportColumnId &&
            item.supportColumnId === currentSelection?.supportColumnId),
      );

      // Workpaper-lazy-load: source the previous citations from the per-cell
      // citation cache (the slim `workpaperData` does not carry them).
      const supportColumnIdForDelete = currentSelection?.supportColumnId;
      const previousCitationsForCell: Citation[] = supportColumnIdForDelete
        ? (getEntry<CellCitationDetail>([
            "procedure-session",
            procedureSessionId,
            "support-cell-citations",
            sourceRowId,
            supportColumnIdForDelete,
          ])?.data?.citations ?? [])
        : [];

      const remaining = previousCitationsForCell.filter((c) => c.id !== id);

      // Workpaper-lazy-load: write the optimistic delete to the per-cell
      // citation cache so the side panel reflects it immediately. The slim
      // `workpaperData` cell entry has no `citations` field, so we do not
      // write to it here.
      if (supportColumnIdForDelete) {
        setEntry<CellCitationDetail>(
          [
            "procedure-session",
            procedureSessionId,
            "support-cell-citations",
            sourceRowId,
            supportColumnIdForDelete,
          ],
          () => ({ citations: remaining }),
        );
      }

      try {
        const normalized: Citation[] = remaining.map((c) => ({
          id: c.id || crypto.randomUUID(),
          documentId: c.documentId,
          encodedCitationString: c.encodedCitationString,
          shortSummary: c.shortSummary ?? null,
          boundingBox: c.boundingBox ?? null,
          pageNumber: c.pageNumber ?? null,
          pageWidth: c.pageWidth ?? null,
          pageHeight: c.pageHeight ?? null,
          citationOrder: c.citationOrder ?? null,
          createdAt: c.createdAt,
          updatedAt: new Date().toISOString(),
        }));

        const supportData: z.infer<typeof SessionSourceRowDataWithCitations> = {
          id: crypto.randomUUID(),
          session_id: auditSession.id,
          attribute_id: currentSelection?.attributeId || null,
          support_column_id: currentSelection?.supportColumnId || null,
          source_row_id: sourceRowId,
          data: currentSelection?.value || "",
          bounding_box: null,
          source_document_id: currentSelection?.documentId || null,
          source_element: null,
          page_number: null,
          page_width: null,
          page_height: null,
          user_id: user?.id || null,
          locked_by: entry?.lockedBy || null,
          column_version: entry?.columnVersion || 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          citations: normalized,
        };

        await fetchFastApi({
          path: `/matching/match-session/${auditSession.id}/source-row/${sourceRowId}/support-data-cell/update-value`,
          method: "POST",
          body: { support_column: supportData },
        });
        toast.success("Annotation deleted");
      } catch (error) {
        console.error("Error deleting bounding box:", error);
        toast.error("Failed to delete bounding box");
        void invalidate(["workpaper-data", procedureSessionId]);
      }
    },
    [
      auditSession,
      currentSelection?.attributeId,
      currentSelection?.documentId,
      currentSelection?.supportColumnId,
      currentSelection?.value,
      currentSelection?.sourceRowId,
      workpaperData?.data,
      user?.id,
      invalidate,
      procedureSessionId,
      getEntry,
      setEntry,
    ],
  );

  // MS-2533: side-panel lock toggle for the selected support cell. Same
  // dedicated lock endpoint and optimistic stamp semantics as the grid's
  // handler in WorkPaperTableV2 — locking never inserts a new row, and it
  // stamps the cell to the current column version (re-affirmed against the
  // current definition) so there is no stale flash on unlock.
  const handleToggleSupportCellLock = useCallback(async () => {
    const sourceRowId = currentSelection?.sourceRowId;
    const supportColumnId = currentSelection?.supportColumnId;
    if (!auditSession || !sourceRowId || !supportColumnId) {
      toast.error("Missing information to toggle lock");
      return;
    }

    const existingData = workpaperData?.data[sourceRowId]?.find(
      (d) => d.supportColumnId === supportColumnId,
    );
    const prevLockedBy = existingData?.lockedBy || null;
    const nextLockedBy = prevLockedBy ? null : user?.id || null;
    const currentColumnVersion = tableShell?.procedure.columns.find(
      (c) => c.supportColumn?.id === supportColumnId,
    )?.columnVersion;

    const flip = (lockedBy: string | null, stampColumnVersion?: number) => {
      setEntry<WorkpaperDataType>(
        ["workpaper-data", procedureSessionId],
        (prev) => {
          const next = { ...prev };
          const list = [...(next.data[sourceRowId] || [])];
          const idx = list.findIndex(
            (i) => i.supportColumnId === supportColumnId,
          );
          if (idx >= 0) {
            list[idx] = {
              ...list[idx],
              lockedBy,
              ...(stampColumnVersion !== undefined
                ? { columnVersion: stampColumnVersion }
                : {}),
            };
            next.data = { ...next.data, [sourceRowId]: list };
          }
          return next;
        },
      );
    };

    flip(nextLockedBy, nextLockedBy ? currentColumnVersion : undefined);
    try {
      await fetchFastApi({
        method: "PUT",
        path: `/matching/match-session/${auditSession.id}/source-row/${sourceRowId}/support-column/${supportColumnId}/lock`,
        body: { locked_by: nextLockedBy },
      });
    } catch (error) {
      console.error("Error toggling lock:", error);
      toast.error("Failed to toggle lock");
      flip(prevLockedBy);
    }
  }, [
    auditSession,
    currentSelection?.sourceRowId,
    currentSelection?.supportColumnId,
    workpaperData?.data,
    tableShell,
    setEntry,
    procedureSessionId,
    user?.id,
  ]);

  const handleSelectNewDocument = useCallback(
    (
      doc: { id: string; fileName?: string | null; fileType?: string },
      opts?: { pageNumber?: number },
    ) => {
      if (!currentSelection?.sourceRowId || !auditSession) return;
      const found = auditSession.documents.find((d) => d.documentId === doc.id);
      if (!found) return;

      updateCurrentDocument({
        id: found.id,
        documentId: found.documentId,
        fileType: found.fetchableDocument?.fileType,
        fileName: found.fetchableDocument?.fileName,
        procedureSessionId: auditSession.id,
      });

      setCurrentSelection((prev) => {
        if (!prev) return prev;
        return { ...prev, documentId: found.documentId };
      });

      if (opts?.pageNumber && opts.pageNumber > 0) {
        setCurrentPage(opts.pageNumber);
      } else {
        setCurrentPage(1);
      }
    },
    [
      auditSession,
      currentSelection?.sourceRowId,
      updateCurrentDocument,
      setCurrentSelection,
    ],
  );

  const lockedBy = useMemo<string | null>(() => {
    if (!currentSelection?.sourceRowId || !currentSelection?.supportColumnId) {
      return null;
    }
    const entry = workpaperData?.data[currentSelection.sourceRowId]?.find(
      (i) => i.supportColumnId === currentSelection.supportColumnId,
    );
    return entry?.lockedBy || null;
  }, [
    currentSelection?.sourceRowId,
    currentSelection?.supportColumnId,
    workpaperData?.data,
  ]);

  // Only blank to a spinner on the INITIAL load (no table-shell yet). On a
  // background refetch — e.g. `invalidate(["procedure-session", id,
  // "table-shell"])` after an outcome edit — `invalidate` keeps the previous
  // `tableShell` data in place while it re-fetches, so we keep rendering it
  // (stale-while-revalidate) rather than collapsing the whole workpaper (and
  // any open fold-out) to a spinner and remounting it.
  if (!tableShell || !auditSession) {
    return (
      <div className="w-full flex items-center justify-center p-8">
        <LoadingSpinner />
      </div>
    );
  }

  // Forge-style inline fold-out: a result selected on THIS procedure (and not
  // the support-column side-panel path) grows the section into a three-pane
  // `grid | steps | viewer` in place of the plain table. Both result kinds open
  // it; the fold-out reads which kind it is over from the selection itself. The
  // support-column path (`showRightPanel`) is excluded so the two never
  // collide. (Computed as `resultFoldOutOpen` above, where the layout-reset
  // effect also needs it.)
  const isResultFoldOut = resultFoldOutOpen;

  // Support cells fold out into the two-pane layout (grid | doc viewer) in
  // place of the legacy right-side doc panel. (Computed as `supportFoldOutOpen`
  // above, where the layout-reset effect also needs it.)
  const isSupportFoldOut = supportFoldOutOpen;

  const workPaperTable = (
    <WorkPaperTableV2
      tableShell={tableShell}
      title={tableShell.procedure.name}
      workpaperData={workpaperData}
      currentSelection={currentSelection}
      onCellSelection={handleCheckSelectionV2}
      onCellEdit={createCellEditHandler(auditSession)}
      showNavigation={false}
      currentTableIndex={0}
      totalTables={1}
      attributes={attributes}
      compactSampleColumn={isResultFoldOut || isSupportFoldOut}
      persistedScrollLeftRef={gridScrollLeftRef}
    />
  );

  return (
    <>
      {workpaperDataLoading && !workpaperData ? (
        // Spinner only on the INITIAL load (no data yet). A background refetch —
        // e.g. useSessionRunsTransitions invalidating ["workpaper-data", id] when
        // a single cell's rerun completes — keeps the previous data in place
        // (invalidate doesn't clear entry.data), so we keep rendering the table
        // and let the values update underneath rather than blanking the whole
        // workpaper to a spinner and remounting it. Mirrors the tableShell gate.
        <div className="flex items-center justify-center p-8">
          <LoadingSpinner />
        </div>
      ) : isResultFoldOut ? (
        // Key by the selected cell so clicking a different cell remounts the
        // fold-out — re-collapsing the grid and restoring the fresh 1/3 : 2/3
        // (steps : viewer) split, exactly like a first cell click.
        <AttributeTestFoldOut
          key={`${currentSelection?.sourceRowId ?? ""}:${currentSelection?.checkResultId ?? ""}`}
          auditSession={auditSession}
          checkResultId={currentSelection?.checkResultId}
          sourceRowId={currentSelection?.sourceRowId}
          onClose={() => setCurrentSelection(undefined)}
          gridPane={workPaperTable}
          gridExpanded={gridExpanded}
          onGridExpandedChange={setGridExpanded}
          paneLayout={paneLayoutRef.current}
          onPaneLayoutChange={(layout) => {
            paneLayoutRef.current = layout;
          }}
          gridScrollTopRef={gridScrollTopRef}
        />
      ) : isSupportFoldOut ? (
        <SupportCellFoldOut
          key={`${currentSelection?.sourceRowId ?? ""}:${currentSelection?.supportColumnId ?? ""}`}
          auditSession={auditSession}
          procedureSessionId={procedureSessionId}
          sourceRowId={currentSelection?.sourceRowId}
          supportColumnId={currentSelection?.supportColumnId}
          value={currentSelection?.value}
          onClose={() => setCurrentSelection(undefined)}
          gridPane={workPaperTable}
          gridScrollTopRef={gridScrollTopRef}
          gridExpanded={supportGridExpanded}
          onGridExpandedChange={setSupportGridExpanded}
          paneLayout={supportPaneLayoutRef.current}
          onPaneLayoutChange={(layout) => {
            supportPaneLayoutRef.current = layout;
          }}
          onLinkingComplete={handleLinkingComplete}
          onDelete={handleDeleteBoundingBox}
          lockedBy={lockedBy}
          currentUserId={user?.id}
        />
      ) : (
        workPaperTable
      )}
      {currentSelection?.procedureSessionId === procedureSessionId &&
        showRightPanel &&
        currentSelection?.sourceRowId &&
        currentSelection?.supportColumnId && (
          <CitationDetailHydrator
            procedureSessionId={procedureSessionId}
            sourceRowId={currentSelection.sourceRowId}
            supportColumnId={currentSelection.supportColumnId}
            setCurrentBoundingBoxes={setCurrentBoundingBoxes}
            setCurrentPage={setCurrentPage}
            setSelectedBoundingIndex={setSelectedBoundingIndex}
          />
        )}
      {currentSelection?.procedureSessionId === procedureSessionId &&
        showRightPanel &&
        sidePanelHost &&
        createPortal(
          <SupportColSidePanelV2
            selectedDocument={selectedDocument}
            currentSelectedDocumentUrl={currentSelectedDocumentUrl}
            currentSelection={currentSelection}
            currentBoundingBoxes={currentBoundingBoxes}
            selectedBoundingIndex={selectedBoundingIndex}
            currentPage={currentPage}
            setSelectedBoundingIndex={setSelectedBoundingIndex}
            setCurrentPage={setCurrentPage}
            onClose={() => setShowRightPanel(false)}
            onLinkingComplete={handleLinkingComplete}
            onDelete={handleDeleteBoundingBox}
            currentAuditSessionId={auditSession.id}
            onSelectNewDocument={handleSelectNewDocument}
            lockedBy={lockedBy}
            onToggleLock={handleToggleSupportCellLock}
          />,
          sidePanelHost,
        )}
      {currentSelection?.procedureSessionId === procedureSessionId &&
        currentSelection?.checkResultId &&
        (() => {
          // An analytics cell's verdict lives in neither `sourceRow.checkResults`
          // nor `attributeTestResults`, so it is dispatched on its own namespace
          // before either arm below can claim it and render "not found".
          if (selectionIsInAnalyticsNamespace(currentSelection.checkResultId)) {
            // The inline fold-out (rendered above in place of the table) carries
            // this verdict's review on its `Workpaper Review` tab, so the sheet
            // would be a second copy of it stacked over the first. The sheet is
            // still the whole experience wherever no fold-out opens — without
            // the auditor-trail flag, chiefly.
            if (isResultFoldOut) {
              return null;
            }
            return (
              <AnalyticsResultAnalysis
                auditSession={auditSession}
                checkResultId={currentSelection.checkResultId}
                sourceRowId={currentSelection.sourceRowId}
                sampleNumber={
                  tableShell.sourceRows.find(
                    (row) => row.id === currentSelection.sourceRowId,
                  )?.sampleNumber
                }
                onClose={() => {
                  setCurrentSelection(undefined);
                }}
                handleCheckSelection={handleCheckSelectionV2}
              />
            );
          }

          const isAttributeTestResult =
            currentSelection.checkResultId?.startsWith("attr-test-");

          if (isAttributeTestResult) {
            // The inline fold-out (rendered above in place of the table)
            // already shows the full attribute-test experience; don't also
            // open the right-Sheet for the same selection.
            if (isResultFoldOut) {
              return null;
            }
            return (
              <AttributeTestAnalysis
                auditSession={auditSession}
                checkResultId={currentSelection?.checkResultId}
                sourceRowId={currentSelection?.sourceRowId}
                evalColumnId={currentSelection?.evalColumnId}
                onClose={() => {
                  setCurrentSelection(undefined);
                }}
                handleCheckSelection={handleCheckSelectionV2}
              />
            );
          }

          return (
            <CheckAnalysisV2
              auditSession={auditSession}
              checkResultId={currentSelection?.checkResultId}
              sourceRowId={currentSelection?.sourceRowId}
              evalColumnId={currentSelection?.evalColumnId}
              onClose={() => {
                setCurrentSelection(undefined);
              }}
              handleCheckSelection={handleCheckSelectionV2}
            />
          );
        })()}
    </>
  );
};
